import React, { useState } from "react";
import { PageHero } from "@/components/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MapPin, Phone, Mail, Send, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { COMPANY_INFO } from "@/data/site";
import { useSEO } from "@/hooks/useSEO";

export default function Contact() {
  useSEO({
    title: "Contact Us — Easyworks Industries Pvt. Ltd.",
    description: "Get in touch with Easyworks Industries. India +91 9473037564 · UAE +971 50 283 9619 · info@easyworksindustries.com. We respond within 24 hours.",
  });
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll get back to you shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="contact-page">
      <PageHero title="Contact Us" subtitle="Have a project in mind? Let's talk. We usually respond within 24 hours." />
      <section className="py-20">
        <div className="container mx-auto max-w-7xl px-6 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-1 space-y-5">
            <div className="p-6 rounded-xl bg-slate-50 border border-slate-100">
              <MapPin className="w-5 h-5 text-[#2563EB] mb-3" />
              <div className="font-semibold text-slate-900 mb-1" style={{ fontFamily: "Outfit" }}>Head Office</div>
              <p className="text-[13.5px] text-slate-600 leading-relaxed">India</p>
              <a href={`tel:${COMPANY_INFO.phoneDial}`} className="text-[13.5px] text-slate-600 hover:text-[#2563EB] block mt-1">{COMPANY_INFO.phone}</a>
            </div>
            <div className="p-6 rounded-xl bg-slate-50 border border-slate-100">
              <MapPin className="w-5 h-5 text-[#2563EB] mb-3" />
              <div className="font-semibold text-slate-900 mb-1" style={{ fontFamily: "Outfit" }}>Regional Office</div>
              <p className="text-[13.5px] text-slate-600 leading-relaxed">United Arab Emirates</p>
              <a href={`tel:${COMPANY_INFO.phoneUAEDial}`} className="text-[13.5px] text-slate-600 hover:text-[#2563EB] block mt-1">{COMPANY_INFO.phoneUAE}</a>
            </div>
            <div className="p-6 rounded-xl bg-slate-50 border border-slate-100">
              <Mail className="w-5 h-5 text-[#2563EB] mb-3" />
              <div className="font-semibold text-slate-900 mb-1" style={{ fontFamily: "Outfit" }}>Email us</div>
              <a href={`mailto:${COMPANY_INFO.email}`} className="text-[13.5px] text-slate-600 hover:text-[#2563EB] break-all">{COMPANY_INFO.email}</a>
            </div>
            <div className="p-6 rounded-xl bg-slate-50 border border-slate-100">
              <Clock className="w-5 h-5 text-[#2563EB] mb-3" />
              <div className="font-semibold text-slate-900 mb-1" style={{ fontFamily: "Outfit" }}>Working Hours</div>
              <p className="text-[13.5px] text-slate-600">Mon – Sat · 9:30 AM to 6:30 PM</p>
            </div>
          </div>

          <div className="lg:col-span-2 p-8 rounded-2xl bg-white border border-slate-200 shadow-sm">
            <div className="mb-6">
              <span className="section-label">Get In Touch</span>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Send us a message</h2>
            </div>
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input id="name" value={form.name} onChange={update("name")} required data-testid="contact-name" />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" value={form.email} onChange={update("email")} required data-testid="contact-email" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" value={form.phone} onChange={update("phone")} data-testid="contact-phone" />
                </div>
                <div>
                  <Label htmlFor="subject">Subject</Label>
                  <Input id="subject" value={form.subject} onChange={update("subject")} data-testid="contact-subject" />
                </div>
              </div>
              <div>
                <Label htmlFor="message">Message *</Label>
                <Textarea id="message" rows={5} value={form.message} onChange={update("message")} required data-testid="contact-message" />
              </div>
              <Button type="submit" disabled={submitting} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-11 px-6 rounded-md gap-2" data-testid="contact-submit-button">
                {submitting ? "Sending..." : (<>Send Message <Send className="w-4 h-4" /></>)}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
